import React, { useState, useEffect } from 'react';
import { IonHeader, IonToolbar, IonTitle, IonContent, IonPage, IonButtons, IonMenuButton, IonList, IonItem, IonAlert, IonIcon, IonAvatar, IonText, IonCard, IonCardContent, IonRow, IonCol, IonGrid, IonLabel, IonInput, IonButton, IonLoading, IonToast } from '@ionic/react';
import { settingsOutline, helpCircleOutline, logOutOutline, personOutline, saveOutline, createOutline } from 'ionicons/icons';
import './Account.scss';
import { setUsername, setEmail, setPhone, setCompany, setPosition } from '../data/user/user.actions';
import { connect } from '../data/connect';
import { RouteComponentProps } from 'react-router';
import UserProfileService from '../services/UserProfileService';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import UserAvatar from '../components/UserAvatar';
import EditableUserAvatar from '../components/EditableUserAvatar';
import { getDefaultAvatar } from '../utils/defaultAvatar';
import ErrorBoundary from '../components/ErrorBoundary';
import SafeIonPage from '../components/SafeIonPage';

interface OwnProps extends RouteComponentProps { }

interface StateProps {
  username?: string;
  userced?: string;
  eventcode?: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
}

interface DispatchProps {
  setUsername: typeof setUsername;
  setEmail: typeof setEmail;
  setPhone: typeof setPhone;
  setCompany: typeof setCompany;
  setPosition: typeof setPosition;
}

interface AccountProps extends OwnProps, StateProps, DispatchProps { }

const Account: React.FC<AccountProps> = ({
  setUsername,
  username,
  userced,
  eventcode,
  email,
  phone,
  company,
  position,
  setEmail,
  setPhone,
  setCompany,
  setPosition
}) => {

  const [showAlert, setShowAlert] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string>('');


  // Estados locales para el formulario
  const [formData, setFormData] = useState({
    username: username || '',
    email: email || '',
    phone: phone || '',
    company: company || '',
    position: position || ''
  });

  const userProfileService = UserProfileService.getInstance();

  // Inicializar formulario con datos del Redux store al montar
  useEffect(() => {
    setFormData({
      username: username || '',
      email: email || '',
      phone: phone || '',
      company: company || '',
      position: position || ''
    });
  }, [username, email, phone, company, position]);

  // Cargar datos desde Firebase cuando cambie el usuario o evento
  useEffect(() => {
    if (userced && eventcode) {
      loadUserProfile();
    }
  }, [userced, eventcode]);

  const loadUserProfile = async () => {
    if (!userced || !eventcode) return;

    setLoading(true);
    try {
      const profile = await userProfileService.getCurrentUserProfile(userced, eventcode);
      if (profile) {
        setFormData({
          username: profile.username || '',
          email: profile.email || '',
          phone: profile.phone || '',
          company: profile.company || '',
          position: profile.position || ''
        });

        // Actualizar Redux store
        setUsername(profile.username);
        setEmail(profile.email);
        setPhone(profile.phone);
        setCompany(profile.company);
        setPosition(profile.position);

        // Cargar foto de perfil
        setProfilePhotoUrl(profile.profilePhotoUrl || '');
      }
    } catch (error) {
      console.error('Error al cargar perfil:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!userced || !eventcode) {
      setToastMessage('Error: No se pudo identificar al usuario');
      setShowToast(true);
      return;
    }

    setLoading(true);
    try {
      const success = await userProfileService.updateUserProfile(userced, eventcode, formData);

      if (success) {
        // Actualizar Redux store
        setUsername(formData.username);
        setEmail(formData.email);
        setPhone(formData.phone);
        setCompany(formData.company);
        // position no se actualiza porque no es editable

        setIsEditing(false);
        setToastMessage('Perfil actualizado exitosamente');
        setShowToast(true);
      } else {
        setToastMessage('Error al actualizar el perfil');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error al guardar perfil:', error);
      setToastMessage('Error al actualizar el perfil');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    // Restaurar datos originales
    setFormData({
      username: username || '',
      email: email || '',
      phone: phone || '',
      company: company || '',
      position: position || ''
    });
    setIsEditing(false);
  };

  const handlePhotoUpdated = (newPhotoUrl: string) => {
    setProfilePhotoUrl(newPhotoUrl);
    setToastMessage('Foto de perfil actualizada');
    setShowToast(true);
  };

  const clicked = (text: string) => {
    console.log(`Clicked ${text}`);
  }

  return (
    <ErrorBoundary>
      <SafeIonPage id="account-page">
        <IonHeader>
          <IonToolbar className="transparent-toolbar">
            <IonButtons slot="start">
              <IonMenuButton></IonMenuButton>
            </IonButtons>
            <IonTitle>Perfil</IonTitle>
          </IonToolbar>
        </IonHeader>
      <IonContent className="ion-padding">
        {username && (
          <>
            {/* Profile header */}
            <div className="profile-header ion-text-center ion-padding" style={{ background: 'rgba(0, 40, 88, 0.85)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '20px' }}>
              <EditableUserAvatar
                size="large"
                onPhotoUpdated={handlePhotoUpdated}
              />

              <IonText color="light">
                <h2 className="profile-name">
                  {username || 'Usuario'}
                </h2>
                <p className="profile-info">{company || 'Participante'}</p>
              </IonText>

              <div className="badge-category-menu ion-margin-vertical">
                <div className="badge-category-item">
                  <span className="badge-category-label">{position || 'Participante'}</span>
                </div>
              </div>
            </div>

            {/* Profile Information Card */}
            <IonCard className="profile-card ion-margin-top" style={{'--background': 'rgba(0, 40, 88, 0.88)', background: 'rgba(0, 40, 88, 0.88)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '20px'} as React.CSSProperties}>
              <IonCardContent>
                <div className="profile-section-header">
                  <h3 style={{color: '#ffffff'}}>Información de Perfil</h3>
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => setIsEditing(!isEditing)}
                    style={{'--color': 'rgba(255,255,255,0.85)'} as React.CSSProperties}
                  >
                    <IonIcon icon={createOutline} slot="start" />
                    {isEditing ? 'Cancelar' : 'Editar'}
                  </IonButton>
                </div>

                <div className="profile-fields">
                  <div className="field-group">
                    <label className="field-label">Nombre Completo</label>
                    <input
                      className="field-input"
                      value={formData.username}
                      placeholder="Ingresa tu nombre completo"
                      readOnly={!isEditing}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                    />
                  </div>

                  <div className="field-group">
                    <label className="field-label">Email</label>
                    <input
                      className="field-input"
                      type="email"
                      value={formData.email}
                      placeholder="tu@email.com"
                      readOnly={!isEditing}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>

                  <div className="field-group">
                    <label className="field-label">Teléfono</label>
                    <input
                      className="field-input"
                      type="tel"
                      value={formData.phone}
                      placeholder="+507 0000-0000"
                      readOnly={!isEditing}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>

                  <div className="field-group">
                    <label className="field-label">Compañía</label>
                    <input
                      className="field-input"
                      value={formData.company}
                      placeholder="Nombre de tu empresa"
                      readOnly={!isEditing}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                    />
                  </div>

                  <div className="field-group readonly">
                    <label className="field-label">Cargo/Posición</label>
                    <input
                      className="field-input"
                      value={formData.position}
                      placeholder="Tu cargo o posición"
                      readOnly={true}
                    />
                  </div>
                </div>

                {isEditing && (
                  <div className="profile-actions ion-margin-top">
                    <IonButton
                      expand="block"
                      fill="solid"
                      onClick={handleSaveProfile}
                      disabled={loading}
                    >
                      <IonIcon slot="start" icon={saveOutline} />
                      Guardar Cambios
                    </IonButton>

                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancelar
                    </IonButton>
                  </div>
                )}
              </IonCardContent>
            </IonCard>

            {/* Settings Card */}
            <IonCard className="settings-card ion-margin-top" style={{'--background': 'rgba(0, 40, 88, 0.88)', background: 'rgba(0, 40, 88, 0.88)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '20px'} as React.CSSProperties}>
              <IonCardContent>
                <h3 style={{color: '#ffffff'}}>Configuración</h3>
                <IonList className="settings-list" lines="none">
                  <IonItem className="setting-item logout-item" routerLink="/logout" routerDirection="none" style={{'--background': 'rgba(235,68,90,0.35)', '--color': '#ffffff'} as React.CSSProperties}>
                    <IonIcon slot="start" icon={logOutOutline} style={{color: '#ffffff'}} />
                    <IonLabel style={{color: '#ffffff', fontWeight: 600}}>Cerrar Sesión</IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>
          </>
        )}

        <IonLoading isOpen={loading} message="Cargando..." />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
        />
      </IonContent>
      <IonAlert
        isOpen={showAlert}
        header="Change Username"
        buttons={[
          'Cancel',
          {
            text: 'Ok',
            handler: (data) => {
              setUsername(data.username);
            }
          }
        ]}
        inputs={[
          {
            type: 'text',
            name: 'username',
            value: username,
            placeholder: 'username'
          }
        ]}
        onDidDismiss={() => setShowAlert(false)}
      />
    </SafeIonPage>
    </ErrorBoundary>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    username: state.user.username,
    userced: state.user.userced,
    eventcode: state.user.eventcode,
    email: state.user.email,
    phone: state.user.phone,
    company: state.user.company,
    position: state.user.position
  }),
  mapDispatchToProps: {
    setUsername,
    setEmail,
    setPhone,
    setCompany,
    setPosition
  },
  component: Account
})